import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { ListContextProvider, useListController, useTranslate } from 'react-admin';

import { GetInstrumentsScheduledEditsItem } from '@Common/types/bo/instruments_scheduled_edits';

import { EmptyScreen, Pagination } from '@sh4res/bo-common/components';
import { ScheduledEditCancelButton } from '@sh4res/bo-common/components/Instruments/sections/ScheduledEdits/components/ScheduledEditCancelButton';
import { getErrorMessage } from '@sh4res/bo-common/utils';

export const InstrumentScheduledEditsTable = ({ instrumentId }: { instrumentId: number }) => {
  const translate = useTranslate();

  const listContext = useListController<GetInstrumentsScheduledEditsItem>({
    resource: 'instruments/scheduled_edits',
    filter: { instrument_id: instrumentId },
    sort: { field: 'scheduled_at', order: 'DESC' },
    perPage: 10,
    disableSyncWithLocation: true,
    queryOptions: { enabled: instrumentId > 0 },
  });

  const { data: scheduledEdits = [], isLoading, error, total } = listContext;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        {getErrorMessage(error)}
      </Alert>
    );
  }

  if (total === 0) {
    return <EmptyScreen label="resources.bo_common.instruments/scheduled_edits.empty" />;
  }

  return (
    <ListContextProvider value={listContext}>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {translate('resources.bo_common.instruments/scheduled_edits.fields.id')}
              </TableCell>
              <TableCell>
                {translate('resources.bo_common.instruments/scheduled_edits.fields.type')}
              </TableCell>
              <TableCell>
                {translate('resources.bo_common.instruments/scheduled_edits.fields.scheduled_at')}
              </TableCell>
              <TableCell>
                {translate('resources.bo_common.instruments/scheduled_edits.fields.status')}
              </TableCell>
              <TableCell align="right">
                {translate('resources.bo_common.instruments/scheduled_edits.fields.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scheduledEdits.map((scheduledEdit) => (
              <TableRow key={scheduledEdit.id} hover>
                <TableCell>{scheduledEdit.id}</TableCell>
                <TableCell>
                  <Chip
                    label={translate(
                      `resources.bo_common.instruments/scheduled_edits.types.${scheduledEdit.type}`,
                    )}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {format(
                    toZonedTime(new Date(scheduledEdit.scheduled_at), 'UTC'),
                    'yyyy-MM-dd HH:mm:ss',
                  )}{' '}
                  UTC
                </TableCell>
                <TableCell>
                  <Chip
                    label={translate(
                      `resources.bo_common.instruments/scheduled_edits.statuses.${scheduledEdit.status}`,
                    )}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  {scheduledEdit.status === 'pending' ? (
                    <ScheduledEditCancelButton
                      scheduledEdit={scheduledEdit}
                      data-testid={`BoExternalInstrumentScheduledEditsRow_${scheduledEdit.id}_CancelButton`}
                    />
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination />
        </Box>
      </Box>
    </ListContextProvider>
  );
};
